import type { PaginatedResult } from "./common";

export interface UserGroupSummary {
	id: string;
	userGroupName: string;
	description?: string; // Optional in UI, might be missing in JSON
	isActive: boolean;
	membersCount: number;
	userGroupType: string;
	members?: UserGroupMember[];
	groupRoles?: {
		id: string;
		roleId: string;
		roleName: string;
		roleType: string;
	}[];
}

export interface UserGroupMember {
	id: string;
	clientUserId: string;
	firstName: string;
	lastName: string;
	email: string;
	displayName: string;
	isActive: boolean;
	joinedDate: string;
}

export interface UserGroupListResult
	extends PaginatedResult<UserGroupSummary> {}

/** Filters for Client `UserGroup/GetAll` (bound by property name). */
export interface UserGroupListQuery {
	pageNumber?: number;
	/** Capped at 50 by the API. */
	pageSize?: number;
	searchTerm?: string;
	userGroupType?: string;
	isActive?: boolean;
}

/** One row of Client `UserGroup/GetAll` as the admin User Groups page reads it. */
export interface UserGroupListItem {
	id: string;
	userGroupName: string;
	userGroupType: string;
	isActive: boolean;
	/** Active memberships. */
	membersCount: number;
	inactiveMembersCount: number;
	/** Active members, sorted; name = "First Last" or the e-mail. */
	members: { clientUserId: string; name: string }[];
	/** Active roles the group grants. */
	roleNames: string[];
	roleCount: number;
	/** Inspection configurations the group inspects (> 0 ⇒ inspector group). */
	inspectorGroupCount: number;
	inspectionConfigurationNames: string[];
	/** Courses the group instructs. */
	instructorGroupCount: number;
}

export interface UserGroupListPage {
	items: UserGroupListItem[];
	totalCount: number;
	pageNumber: number;
	pageSize: number;
}

export enum UserGroupType {
	Users = "Users",
	Inspectors = "Inspectors",
	Instructors = "Instructors",
	Other = "Other",
	Managers = "Managers",
	Admins = "Admins",
}

export interface CreateUserGroupRequest {
	userGroupName: string;
	userGroupType: UserGroupType;
	isActive: boolean;
	memberIds: string[];
	roleIds: string[];
}

export interface UpdateUserGroupRequest {
	userGroupName: string;
	userGroupType: UserGroupType;
	description?: string;
	isActive: boolean;
}

export interface UserGroupRole {
	roleId: string;
	roleName: string;
	roleType: string;
}

export interface AssignRoleRequest {
	roleId: string;
	// clientId? usually inferred or required by backend DTO, but service handles logic
}
